import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import router from '@plugins/router';
import { AuthRoutes, SharedRoutes } from '@/types/enums/RouteNameEnums';
import { getApiBaseUrl } from '@/envProvider';
import { useWasteViewStore } from '@/stores/wasteViewStore';

export const useAxios = (baseURL?: string) => {
    const instance = axios.create({ baseURL: baseURL ?? getApiBaseUrl() });

    instance.interceptors.request.use(
        async config => {
            const authStore = useAuthStore();

            if (authStore.authToken) {
                config.headers.Authorization = `Bearer ${authStore.authToken.access_token}`;
            }

            // waste collection corrected by the AI photo check (switch on its pages, see wasteViewStore)
            if (config.url?.includes('waste-collection/')) {
                const wasteView = useWasteViewStore();

                if (wasteView.aiCorrected) {
                    config.params = { ...config.params, view: 'ai' };
                }

                // staff only (the API ignores it otherwise): also the re-reads of emptied bins
                if (wasteView.allReads) {
                    config.params = { ...config.params, reads: 'all' };
                }
            }

            return config;
        },
        error => Promise.reject(error),
    );

    instance.interceptors.response.use(
        response => response,
        async error => {
            const authStore = useAuthStore();

            if (error.response?.status === 401) {
                if (authStore.isImpersonating && !error.config._returningToSelf) {
                    if (authStore.originalAuthToken && authStore.originalUser) {
                        authStore.authToken = { ...authStore.originalAuthToken };
                        authStore.user = { ...authStore.originalUser };
                        authStore.isImpersonating = false;
                        authStore.originalUser = null;
                        authStore.originalAuthToken = null;

                        localStorage.setItem('auth_token', JSON.stringify(authStore.authToken));
                        localStorage.setItem('user_data', JSON.stringify(authStore.user));
                        localStorage.removeItem('is_impersonating');
                        localStorage.removeItem('original_user');
                        localStorage.removeItem('original_auth_token');

                        router.push({ name: SharedRoutes.Users });

                        return Promise.reject(error);
                    } else {
                        authStore.clearSession();
                        router.push({ name: AuthRoutes.Login });
                    }
                } else if (authStore.authToken?.refresh_token && !error.config._retried && !authStore.isImpersonating) {
                    try {
                        error.config._retried = true;
                        await authStore.refreshToken();
                        error.config.headers.Authorization = `Bearer ${authStore.authToken.access_token}`;

                        return axios.request(error.config);
                    } catch {
                        authStore.clearSession();
                        router.push({ name: AuthRoutes.Login });
                    }
                } else {
                    authStore.clearSession();
                    router.push({ name: AuthRoutes.Login });
                }
            }

            if (error.response?.status === 403) {
                router.currentRoute.value.meta.accessDenied = true;
            }

            if (error.response?.status === 404) {
                // TODO: Redirect to 404 error page
                console.error('Resource not found');
            }

            return Promise.reject(error);
        },
    );

    return instance;
};
